// Presentational state of a postventa observation, derived from the
// engine's phase projection — never from `observationStatus` compared
// against a literal nor from `observationCycle` compared against a
// number (fitness: observation-phase-single-source).
//
// The wire (`postventaObservationSchema`, `findingDetailResponse`)
// carries `observationPhase` + `reopened` computed server-side by
// `observationPhase()` from the recipe graph. A row from an older
// fixture or a core-api build that predates those fields falls back to
// calling the SAME engine function client-side with the recipe from
// `useRecipe()`; the label table below is the only mapping this module
// owns, and it maps phases, not status keys.
//
// «No conforme» is not a status: it is an EVENT of the thread. The row
// it leaves behind is `triage` again, painted «Solicitada» plus the
// modifier «Reabierta ×N» (README §2). The modifier rides `reopenCount`
// — derived once in the engine, carried on the wire — and shows in ANY
// state with at least one reopening, not only in triage. «Cerrada» vs
// «Rechazada» split the `closed` phase on the closure overlay (stamped
// = the receiving party closed it; otherwise the graph's terminal node —
// the rejection — ended it).
//
// Labels and tones are NOT owned here: they come from the shared module
// `observation-labels.ts` in @bitacorapro/api-types (`estadoKindFor`,
// `estadoFor`, `estadoLabelsFor`), the same one the PWA reads. With the
// recipe in hand the label is the recipe's own (ADR-0134: facility
// paints «Abierta» / «Resuelta» / «Anulada»); without it, the postventa
// table. This module only adds the dashboard's bucket/pending reading of
// each kind.

import {
  estadoFor,
  estadoKindFor,
  estadoLabelsFor,
  observationPhase,
  rejectionAwaitsReceiverFor,
  type EstadoLabels,
  type ObservacionEstadoKind,
  type ObservationPhase,
  type ObservationPhaseVocabulary,
  type PillTone,
} from "@bitacorapro/api-types";

export type Bucket = "all" | "pendientes" | "proveedor" | "conformidad" | "apeladas" | "cerradas";

/** What the row IS, as a stable code — branch on this, never on
 *  `label`, which is presentational copy and may change wording. Same
 *  code as the shared module's `ObservacionEstadoKind`. */
export type PresentationalKind = ObservacionEstadoKind;

export type PresentationalState = {
  kind: PresentationalKind;
  /** The engine phase the kind was derived from (server wire first,
   *  engine fallback). `closed` covers both the conformity overlay and
   *  the graph's terminal node — what `isEntryOpen` reads. */
  phase: ObservationPhase;
  /** «Solicitada», «Aceptada», … from `ESTADO_LABEL`. */
  label: string;
  /** Semantic tone; map it with `ST_PILL_CLASS` (./pills). */
  tone: PillTone;
  /** Reopenings by non-conformity (0 = never reopened). Drives the
   *  «Reabierta ×N» modifier in any state. */
  reopenCount: number;
  /** Cycle number = reopenCount + 1. */
  ciclo: number;
  /** The inmobiliaria has something to do (accept / reject / assign). */
  pendiente: boolean;
  bucket: Exclude<Bucket, "all">;
};

/** The minimum a row must carry. `observationPhase` / `reopened` /
 *  `reopenCount` are optional on the wire (ADR-0021 D6); the rest feeds
 *  the fallback. */
export type PhaseInput = {
  observationPhase?: ObservationPhase | null | undefined;
  reopened?: boolean | null | undefined;
  /** ADR-0141 — apelación del rechazo en curso (modificador derivado,
   *  mismo patrón que `reopened`). */
  appealed?: boolean | null | undefined;
  reopenCount?: number | null | undefined;
  observationStatus: string | null;
  observationClosedAt?: string | null | undefined;
  /** Any closure reason («conformidad», «auto_approval», «migrated»):
   *  the kind only asks whether the overlay is stamped. */
  observationClosedReason?: string | null | undefined;
  observationCycle?: number | null | undefined;
  originCode?: string | null | undefined;
};

type Resolved = {
  phase: ObservationPhase;
  reopenCount: number;
  reopened: boolean;
  appealed: boolean;
};

/** Prefer the server projection; fall back to the engine with the
 *  recipe when the row predates the wire fields. Without either there is
 *  nothing to derive from, and the row reads as triage — the phase a
 *  NULL status resolves to in the engine too. */
function resolvePhase(o: PhaseInput, vocab: ObservationPhaseVocabulary | undefined): Resolved {
  // The engine runs only for what the wire did not bring.
  const needEngine = o.observationPhase == null || o.reopenCount == null;
  const engine =
    vocab && needEngine
      ? observationPhase(vocab, {
          observationStatus: o.observationStatus,
          observationClosedAt: o.observationClosedAt ?? null,
          observationCycle: o.observationCycle ?? null,
          originCode: o.originCode ?? null,
        })
      : null;
  const phase = o.observationPhase ?? engine?.phase ?? "triage";
  // An older core-api sends `reopened` but not `reopenCount`: without
  // the recipe to re-derive, a reopened row has at least one reopening.
  const reopenCount = o.reopenCount ?? engine?.reopenCount ?? (o.reopened === true ? 1 : 0);
  // The engine's `reopened` (entry phase ∧ cycle > 1, ADR-0134): only
  // ever true outside triage in a no-triage graph, where it turns an
  // awaiting_assignment row into «Reabierta». Wire first, then engine.
  const reopened = o.reopened ?? engine?.reopened ?? false;
  const appealed = o.appealed ?? engine?.appealed ?? false;
  return { phase, reopenCount, reopened, appealed };
}

const BUCKET: Record<PresentationalKind, Exclude<Bucket, "all">> = {
  open: "pendientes",
  reopened: "pendientes",
  accepted: "pendientes",
  assigned: "proveedor",
  resolved: "conformidad",
  conforme: "cerradas",
  // ADR-0142 — cerrada por falta de respuesta: al archivo, como toda
  // cerrada (el conteo aparte lo da /postventa-dashboard/closure-summary).
  closed_no_response: "cerradas",
  // ADR-0141: «rechazada» espera la respuesta del propietario — misma
  // cola que la conformidad (lo que espera al propietario), no un
  // cierre. La apelación es la cola propia del jefe («Apeladas»), y el
  // cierre del carril (rechazada_cerrada, o una rechazada legacy
  // cerrada) sí va al archivo.
  rejected: "conformidad",
  appealed: "apeladas",
  rejected_closed: "cerradas",
};

// One derivation per vocabulary object: the recipe from useRecipe() is a
// stable reference, so a list of N rows walks the graph once, not N times.
const labelsCache = new WeakMap<ObservationPhaseVocabulary, EstadoLabels>();
function labelsOf(vocab: ObservationPhaseVocabulary | undefined): EstadoLabels | undefined {
  // A partial fixture or a recipe still loading carries no vocabulary.
  if (!vocab?.observationStatuses) return undefined;
  let labels = labelsCache.get(vocab);
  if (!labels) {
    labels = estadoLabelsFor(vocab);
    labelsCache.set(vocab, labels);
  }
  return labels;
}

export function presentationalState(
  o: PhaseInput,
  vocab?: ObservationPhaseVocabulary
): PresentationalState {
  const { phase, reopenCount, reopened, appealed } = resolvePhase(o, vocab);
  const kind = estadoKindFor({
    phase,
    reopenCount,
    reopened,
    appealed,
    closedAt: o.observationClosedAt ?? null,
    closedReason: o.observationClosedReason ?? null,
    // Recipe-shape discriminator (ADR-0141): a graph without the
    // rejection-wait lane (facility) keeps its terminal rejection on
    // kind `rejected` — same red pill as before the lane existed.
    rejectionAwaitsReceiver: rejectionAwaitsReceiverFor(vocab),
  });
  const { label, tone } = estadoFor(kind, labelsOf(vocab));
  const bucket = BUCKET[kind];
  return {
    kind,
    phase,
    label,
    tone,
    reopenCount,
    ciclo: reopenCount + 1,
    pendiente: bucket === "pendientes",
    bucket,
  };
}

/** The state pill's text: only the label. The cycle travels in the
 *  separate «Reabierta ×N» modifier pill (see ObservacionPills). */
export function presentationalStateText(s: PresentationalState): string {
  return s.label;
}

export function bucketOf(
  o: PhaseInput,
  vocab?: ObservationPhaseVocabulary
): Exclude<Bucket, "all"> {
  return presentationalState(o, vocab).bucket;
}

export function countPendientes(
  rows: readonly PhaseInput[],
  vocab?: ObservationPhaseVocabulary
): number {
  return rows.filter((o) => presentationalState(o, vocab).pendiente).length;
}

export type GroupCounts = Record<Bucket, number> & {
  /** Split of `pendientes` for the KPI hint
   *  («N por aceptar · M por asignar · K apeladas»). */
  porAceptar: number;
  porAsignar: number;
};

export function groupCounts(
  rows: readonly PhaseInput[],
  vocab?: ObservationPhaseVocabulary
): GroupCounts {
  const counts: GroupCounts = {
    all: rows.length,
    pendientes: 0,
    proveedor: 0,
    conformidad: 0,
    apeladas: 0,
    cerradas: 0,
    porAceptar: 0,
    porAsignar: 0,
  };
  for (const o of rows) {
    const s = presentationalState(o, vocab);
    counts[s.bucket] += 1;
    // «Por aceptar» = the entry lane proper: triage never reopened.
    // «Por asignar» = the next action is an assignment: an accepted row
    // and a reopened one (the reassignment composes accept + assign
    // server-side). An appealed row (ADR-0141) lists under its own
    // «Apeladas» bucket but still waits on the jefe, and the server
    // rollups (`pendingTriage`/`pendingObservations`) deliberately keep
    // counting it as pendiente — so the KPI total here adds it back
    // (outside the por aceptar / por asignar split; the hint shows it as
    // «K apeladas») and the card, tile, badge and this list agree.
    if (s.kind === "open") counts.porAceptar += 1;
    else if (s.kind === "accepted" || s.kind === "reopened") counts.porAsignar += 1;
    else if (s.kind === "appealed") counts.pendientes += 1;
  }
  return counts;
}

/** «Pendientes de gestión» de un rollup del servidor (card del proyecto,
 *  tile del detalle, badge del sidebar): lo que espera una acción de la
 *  inmobiliaria. `pendingObservations` viene deduplicado por el servidor
 *  y es la fuente; la suma `pendingTriage + unassignedObservations` es
 *  sólo el respaldo de un core-api que todavía no lo manda (desde que
 *  `pendingTriage` excluye las reabiertas, ambas coinciden). */
export function pendientesDeGestion(p: {
  pendingObservations?: number | undefined;
  pendingTriage?: number | undefined;
  unassignedObservations?: number | undefined;
}): number {
  if (p.pendingObservations !== undefined) return p.pendingObservations;
  return (p.pendingTriage ?? 0) + (p.unassignedObservations ?? 0);
}
